import { Fragment, ReactNode, useCallback, useContext } from 'react';
import { isMobile } from '@direct-frontend/platform-code';

import { Tag } from '../../../Tag/Tag';
import { TagGroupTagProps } from '../../types';
import { cn } from '../../../../lib/css/className/className';
import { TagGroupContext } from '../../utils';
import { TestProps } from '../../../../internal/types/testTypes';
import { getSizeSkin } from '../../../../lib/skin/getSizeSkin/getSizeSkin';
import './TagsWrapper.css';
import { getColorSkin } from '../../../../lib/skin/getColorSkin/getColorSkin';

export interface TagsWrapperProps extends TestProps {
    /**
     * Список выбранных тегов
     */
    tags: TagGroupTagProps[];

    /**
     * Индекс выбранного тега
     */
    selectedTagIndex?: number;

    /**
     * Обработчик нажатия на кнопку удаления тега
     */
    onRemove?: (tagProps: TagGroupTagProps, index: number) => void;

    /**
     * Обработчик нажатия на тег
     */
    onClick?: (index: number) => void;

    /**
     * Редактор тегов
     */
    editor?: ReactNode;

    /**
     * Дочерние компоненты
     */
    children?: ReactNode;
    /**
     * Максимальная высота области с тегами
     */
    maxInputHeight?: string;
}

const cls = cn('TagsWrapper');
const cnTagGroup = cn('TagGroup');

function TagWrapper(
    props: Omit<TagGroupTagProps, 'onClick' | 'onClose'> & {
        index: number;
        onRemove?: (tagProps: TagGroupTagProps, index: number) => void;
        onClick?: (index: number) => void;
    },
) {
    const { index, onClick, onRemove, ...tagProps } = props;
    const { isEditable, isDisabled, size, 'data-testid': dataTestId } = useContext(TagGroupContext);
    const defaultDataTestid = props.title ?? (typeof props.tag === 'string' ? props.tag : props.index);

    const clickHandler = useCallback(() => {
        onClick?.(index);
    }, [index, onClick]);

    const onClose = useCallback(() => {
        onRemove?.(tagProps, index);
    }, [onRemove, tagProps, index]);

    return (
        <Tag
            {...props}
            onClick={!props.isReadonly && isEditable ? clickHandler : undefined}
            data-testid={props['data-testid'] ?? `${dataTestId ?? 'TagGroup'}.tag.${defaultDataTestid}`}
            onClose={onClose}
            isDisabled={isDisabled || props.isDisabled}
            size={isMobile() ? 'm' : size}
            className={cnTagGroup('tag')}
        />
    );
}

/**
 * Компонент для отображения списка тегов
 */
export const TagsWrapper: React.FC<TagsWrapperProps> = ({
    tags,
    onRemove,
    onClick,
    selectedTagIndex,
    editor,
    children,
    'data-testid': dataTestId,
    maxInputHeight,
}) => {
    const { size } = useContext(TagGroupContext);
    const sizeSkinClass = getSizeSkin(isMobile() ? 'm' : (size ?? 'm'));
    const colorSkinClassName = getColorSkin('gray-neutral', {});
    return (
        <div
            className={cls('', { scrollable: Boolean(maxInputHeight) }, [
                sizeSkinClass,
                colorSkinClassName,
                cnTagGroup(),
            ])}
            data-testid={dataTestId}
            style={maxInputHeight ? { maxHeight: maxInputHeight } : undefined}
        >
            {tags.map((tagProps, index) => {
                if (editor && index === selectedTagIndex) {
                    return <Fragment key={index}>{editor}</Fragment>;
                }

                const { key, ...restTagProps } = tagProps;

                return (
                    <TagWrapper
                        key={key ?? index.toString()}
                        {...restTagProps}
                        index={index}
                        onRemove={onRemove}
                        onClick={onClick}
                    />
                );
            })}
            {children}
        </div>
    );
};
